import type { ClipboardEvent } from "react";
import { toast } from "./toast";

/**
 * Collection of input filter functions for form validation
 * These functions sanitize user input in real-time to prevent invalid characters
 */

/**
 * Removes accents from a string while preserving ñ/Ñ characters.
 * The ñ is preserved by temporarily replacing it before NFD normalization,
 * since NFD decomposes ñ into "n" + combining tilde which would be removed.
 */
const removeAccentsPreserveNTilde = (value: string): string => {
  if (!value) return value;
  const preserved = value.replace(/ñ/g, "\u0000").replace(/Ñ/g, "\u0001");
  const withoutAccents = preserved
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return withoutAccents.replace(/\u0000/g, "ñ").replace(/\u0001/g, "Ñ");
};

/**
 * Filters a string to allow only letters (including ñ/Ñ and optionally accented chars) and spaces.
 * When allowAccents=false (default): Removes accented characters (á, é, í, ó, ú, ü, etc.)
 * When allowAccents=true: Preserves accented characters
 * Use for: First names, last names, city names
 */
export const filterNameInput = (
  value: string,
  allowAccents = false,
): string => {
  if (!value) return value;
  if (allowAccents) {
    return value;
  }
  const withoutAccents = removeAccentsPreserveNTilde(value);
  return withoutAccents.replace(/[^a-zA-ZñÑ\s]/g, "");
};

/**
 * Filters a string to allow letters, numbers, spaces, and common address punctuation (.,#-)
 * When allowAccents=false (default): Removes accented characters
 * When allowAccents=true: Preserves accented characters
 * Use for: Street addresses
 */
export const filterStreetInput = (
  value: string,
  allowAccents = false,
): string => {
  if (!value) return value;
  if (allowAccents) {
    return value;
  }
  const withoutAccents = removeAccentsPreserveNTilde(value);
  return withoutAccents.replace(/[^a-zA-ZñÑ0-9\s.,#-]/g, "");
};

/**
 * Filters a string to allow only alphanumeric characters and hyphens
 * Use for: Exterior/interior numbers
 */
export const filterAddressNumber = (value: string): string => {
  if (!value) return value;
  return value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 10);
};

/**
 * Filters a string to allow letters, numbers, spaces, and hyphens
 * When allowAccents=false (default): Removes accented characters
 * When allowAccents=true: Preserves accented characters
 * Use for: Property alias
 */
export const filterPropertyAlias = (
  value: string,
  allowAccents = false,
): string => {
  if (!value) return value;
  if (allowAccents) {
    return value.slice(0, 50);
  }
  const withoutAccents = removeAccentsPreserveNTilde(value);
  return withoutAccents.replace(/[^a-zA-ZñÑ0-9\s-]/g, "").slice(0, 50);
};

/**
 * Filters and formats RFC for companies (Persona Moral)
 * Automatically converts to uppercase and limits to valid RFC characters
 * Use for: Company RFC input
 */
export const filterCompanyRFC = (value: string): string => {
  if (!value) return value;
  return value
    .toUpperCase()
    .replace(/[^A-ZÑ0-9&]/g, "")
    .slice(0, 13);
};

/**
 * Filters legal entity names to allow alphanumeric, ampersand, ñ/Ñ, and spaces
 * When allowAccents=false (default): Removes accented characters
 * When allowAccents=true: Preserves accented characters
 * Use for: Company legal names (Razón Social)
 */
export const filterLegalEntityName = (
  value: string,
  allowAccents = false,
): string => {
  if (!value) return value;
  if (allowAccents) {
    return value.slice(0, 120);
  }
  const withoutAccents = removeAccentsPreserveNTilde(value);
  return withoutAccents.replace(/[^a-zA-Z0-9&ñÑ\s]/g, "").slice(0, 120);
};

/**
 * Filters to allow only numeric digits
 * Use for: Phone numbers, zip codes, numeric IDs
 */
export const filterNumericInput = (
  value: string,
  maxLength?: number,
): string => {
  const numeric = value.replace(/\D/g, "");
  return maxLength ? numeric.slice(0, maxLength) : numeric;
};

/**
 * Filters city/state names to allow only letters and spaces
 * When allowAccents=false (default): Removes accented characters
 * When allowAccents=true: Preserves accented characters
 * Use for: City names, state names
 */
export const filterCityInput = (
  value: string,
  allowAccents = false,
): string => {
  if (!value) return value;
  if (allowAccents) {
    return value.slice(0, 80);
  }
  const withoutAccents = removeAccentsPreserveNTilde(value);
  return withoutAccents.replace(/[^a-zA-ZñÑ\s]/g, "").slice(0, 80);
};

/**
 * Shows a toast only when the filtered value removed accents or invalid characters
 */
export const notifyAccentRemoved = (
  original: string,
  filtered: string,
  { duration = 2000 }: { duration?: number } = {},
): void => {
  if (original !== filtered && original.trim() !== "") {
    toast.info("Se removieron acentos automáticamente", { duration });
  }
};

/**
 * Formats a 10-digit phone number for display as (555) 123-4567
 * Use for: Displaying phone numbers in a user-friendly format
 */
export const formatPhoneDisplay = (value: string): string => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length === 0) return "";
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6)
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Validates email format in real-time (basic regex check)
 * Use for: Providing instant visual feedback on email fields
 */
export const isEmailFormatValid = (email: string): boolean => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Sanitizes email input by removing all accents (including ñ),
 * converting to lowercase, and trimming whitespace.
 * Use for: Email input fields to ensure clean, valid email addresses
 */
export const sanitizeEmailInput = (value: string): string => {
  if (!value) return value;
  // Remove ALL accents including ñ/Ñ for emails
  const withoutAccents = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // Convert to lowercase and trim
  return withoutAccents.toLowerCase().trim();
};

const removeAccentsFromString = (value: string): string =>
  removeAccentsPreserveNTilde(value);

const scheduleSelectionRestore = (callback: () => void): void => {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(callback);
    return;
  }

  setTimeout(callback, 0);
};

const buildPastedValue = (
  base: string,
  insertion: string,
  selectionStart: number | null,
  selectionEnd: number | null,
): { nextValue: string; cursor: number } => {
  const start = selectionStart ?? base.length;
  const end = selectionEnd ?? base.length;

  const nextValue =
    base.substring(0, start) + insertion + base.substring(end);

  return { nextValue, cursor: start + insertion.length };
};

/**
 * Handles paste events with optional accent removal
 * When allowAccents=true: Preserves accents in pasted text
 * When allowAccents=false (default): Removes accents from pasted text
 *
 * @example
 * <Input
 *   onPaste={(e) => handlePasteWithAccentOption(e, (v) => filterNameInput(v, true), setValue, true)}
 * />
 */
export const handlePasteWithAccentOption = (
  e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  filterFunction?: (value: string) => string,
  setValue?: (value: string) => void,
  allowAccents = false,
): void => {
  e.preventDefault();

  const target = e.target as HTMLInputElement | HTMLTextAreaElement;
  if (!target) return;

  const pastedText = e.clipboardData.getData("text");
  let processedText = pastedText;

  if (!allowAccents) {
    processedText = removeAccentsFromString(pastedText);
    // Show notification ONLY when pasting text with accents and not allowing them
    if (pastedText !== processedText && pastedText.trim() !== "") {
      toast.info("Se removieron automáticamente los acentos", {
        duration: 2000,
      });
    }
  }

  const filteredText = filterFunction
    ? filterFunction(processedText)
    : processedText;

  const { nextValue, cursor } = buildPastedValue(
    target.value,
    filteredText,
    target.selectionStart,
    target.selectionEnd,
  );

  if (setValue) {
    setValue(nextValue);
    scheduleSelectionRestore(() => {
      target.setSelectionRange(cursor, cursor);
    });
    return;
  }

  target.value = nextValue;
  target.setSelectionRange(cursor, cursor);

  const event = new Event("input", { bubbles: true });
  target.dispatchEvent(event);
};

/**
 * Handles paste events to remove accents before inserting text
 * Shows a notification if accents were removed from pasted text
 * Use with onPaste event handler
 *
 * @example
 * <Input
 *   onPaste={(e) => handlePasteWithoutAccents(e, filterNameInput, setValue)}
 * />
 */
export const handlePasteWithoutAccents = (
  e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  filterFunction?: (value: string) => string,
  setValue?: (value: string) => void,
): void => {
  e.preventDefault();

  const target = e.target as HTMLInputElement | HTMLTextAreaElement;
  if (!target) return;

  const pastedText = e.clipboardData.getData("text");
  const withoutAccents = removeAccentsFromString(pastedText);
  const filteredText = filterFunction
    ? filterFunction(withoutAccents)
    : withoutAccents;

  const { nextValue, cursor } = buildPastedValue(
    target.value,
    filteredText,
    target.selectionStart,
    target.selectionEnd,
  );

  // Show notification ONLY when pasting text with accents
  if (pastedText !== withoutAccents && pastedText.trim() !== "") {
    toast.info("Se removieron automáticamente los acentos", {
      duration: 2000,
    });
  }

  if (setValue) {
    // Keep controlled values in sync without mutating the DOM directly
    setValue(nextValue);
    scheduleSelectionRestore(() => {
      target.setSelectionRange(cursor, cursor);
    });
    return;
  }

  // For uncontrolled or form-managed inputs update the DOM and notify React manually
  target.value = nextValue;
  target.setSelectionRange(cursor, cursor);

  const event = new Event("input", { bubbles: true });
  target.dispatchEvent(event);
};
